import React from 'react';
import styled from 'styled-components';
import sx from '../../sx';
import { Card, Text, Link, themeGet } from '../..';

/**
 * Every Getting Started page ListCard provides links that give users actions to take.
 *
 * Those actions can be internal, external, or addon actions.
 */

/**
 * The type of analytics metrics your application collects.
 */

/**
 * The ListCard component is a border-less `Card` component that provides detail about key functionality, and link to documentation
 * that helps people understand how to use your product.
 * 
 * You'll want to ensure that you provide an array of list items, each of which should adhere to the following shape:
 * 
 * ```typescript
 * 
 * type ListCardItem = {
    itemPrimaryAction: ListCardAction
    itemDocsAction: ListCardAction
    label?: string
    description?: string
}
 * ```
 *
 * The `ListCardAction` shares an identical shape to the other Getting Started Page component action types:
 * 
 * ```typescript
 * type ListCardAction = {
 *  type: 'EXTERNAL' | 'INTERNAL' | 'ADDON'
      url: string
      label: string
      tracking: {
        destination: string
        type: 'button' | 'link' | 'video'
      }
 * }
 *```
 */
const ListCard = ({
  title,
  caption,
  primaryAction,
  actionHandler,
  listItems
}) => {
  return /*#__PURE__*/React.createElement(Wrapper, {
    sx: {
      flex: 1
    },
    as: "article"
  }, /*#__PURE__*/React.createElement(Card.Content, null, /*#__PURE__*/React.createElement(Card.Header, null, /*#__PURE__*/React.createElement(Text, {
    as: "h3",
    sx: {
      fontWeight: 700,
      fontSize: 4,
      lineHeight: 24 / 20,
      margin: 'auto',
      color: 'text.default'
    }
  }, title)), /*#__PURE__*/React.createElement(Card.Body, {
    sx: {
      minHeight: '140px',
      fontSize: 2,
      lineHeight: 'normal',
      color: 'text.subtle'
    }
  }, /*#__PURE__*/React.createElement(Text, {
    as: "p",
    sx: {
      lineHeight: 20 / 14,
      marginTop: '10px'
    }
  }, caption), /*#__PURE__*/React.createElement(StyledList, null, listItems === null || listItems === void 0 ? void 0 : listItems.map(item => {
    return /*#__PURE__*/React.createElement(StyledItem, {
      key: item.label
    }, /*#__PURE__*/React.createElement(StyledSpan, null, /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(StyledLink, {
      href: item.itemPrimaryAction.url,
      onClick: () => actionHandler ? actionHandler(item.itemPrimaryAction) : null
    }, item.label), "\xA0", item.description), /*#__PURE__*/React.createElement(Link, {
      href: item.itemDocsAction.url,
      onClick: () => actionHandler ? actionHandler(item.itemDocsAction) : null,
      sx: {
        fontStyle: 'normal',
        fontWeight: 'normal'
      }
    }, "(documentation)")));
  })))));
};
ListCard.displayName = "ListCard";
const Wrapper = styled(Card).withConfig({
  displayName: "ListCard__Wrapper",
  componentId: "sc-190haaj-0"
})(["@media (min-width:", "){max-width:385px;}@media (min-width:", "){max-width:450px;}", ""], themeGet('breakpoints.1'), themeGet('breakpoints.2'), sx);
const StyledList = styled.ul.withConfig({
  displayName: "ListCard__StyledList",
  componentId: "sc-190haaj-1"
})(["margin-top:10px;", ""], sx);
const StyledItem = styled.li.withConfig({
  displayName: "ListCard__StyledItem",
  componentId: "sc-190haaj-2"
})(["margin-top:10px;", ""], sx);
const StyledSpan = styled.span.withConfig({
  displayName: "ListCard__StyledSpan",
  componentId: "sc-190haaj-3"
})(["display:flex;flex-direction:column;", ""], sx);
const StyledLink = styled(Link).withConfig({
  displayName: "ListCard__StyledLink",
  componentId: "sc-190haaj-4"
})(["color:", ";&:visited{color:", ";}", ";"], themeGet('colors.text.default'), themeGet('colors.text.default'), sx);
export default ListCard;